import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { adminApi } from "../../lib/auth";
import FilterBar from "../../components/admin/FilterBar";
import { toast } from "sonner";

const downloadCsv = async (endpoint, filename, params) => {
  const qs = new URLSearchParams(params).toString();
  const token = localStorage.getItem("dvp_admin_token");
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}${endpoint}?${qs}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return toast.error("Export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminChats() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await adminApi.get("/admin/chat-messages", { params });
      setItems(data.items);
      setCount(data.count);
    } catch {
      toast.error("Failed to load chats");
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const onExport = () => {
    const params = {};
    if (q) params.q = q;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    downloadCsv(
      "/api/admin/chat-messages/export",
      `chats_${new Date().toISOString().slice(0, 10)}.csv`,
      params
    );
  };

  return (
    <div className="p-8 space-y-6" data-testid="admin-chats-page">
      <div>
        <h1 className="font-display font-bold text-3xl text-white tracking-tight">
          Chat Messages
        </h1>
        <p className="text-neutral-400 text-sm mt-1">
          Conversations from the floating chat widget.
        </p>
      </div>

      <FilterBar
        q={q}
        setQ={setQ}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        count={count}
        onRefresh={load}
        onExport={onExport}
        searchPlaceholder="Search by name, contact or message…"
      />

      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] border-b border-white/10">
              <tr className="text-left text-[11px] uppercase font-mono tracking-[0.2em] text-neutral-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Message</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin inline text-[#dc2626]" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-neutral-500">
                    No messages yet
                  </td>
                </tr>
              ) : (
                items.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-neutral-400 font-mono text-xs whitespace-nowrap">
                      {new Date(m.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-white">{m.name || "Anonymous"}</td>
                    <td className="px-4 py-3 text-neutral-300 font-mono text-xs">
                      {m.contact || "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-200 max-w-md">{m.message}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
